import type { SocialCampaignReportResponse } from "../../types";

export type SocialCampaignView = "active" | "archive" | "all";

type AggregatedAdCreative = {
  ad_name: string;
  total_reach: number;
  total_impressions: number;
  total_spend: number;
  total_clicks: number;
  total_engagement: number;
  total_landing_page_views: number;
  total_reactions: number;
  total_comments: number;
  total_shares: number;
  campaign_names: string[];
  ad_set_names: string[];
  platforms: string[];
  avg_ctr: number | null;
  variant_names: string[];
};

type AudienceCreativeRow = {
  ad_name: string;
  total_reach: number;
  total_impressions: number;
  total_engagement: number;
  total_spend: number;
  total_clicks: number;
  avg_ctr: number | null;
};

type AudienceCreativePerformance = {
  ad_set_name: string;
  total_reach: number;
  total_impressions: number;
  total_engagement: number;
  creatives: AudienceCreativeRow[];
};

type SocialCampaign = SocialCampaignReportResponse["campaigns"][number];
type SocialAdSet = SocialCampaignReportResponse["ad_sets"][number];
type SocialAdCreativeRecord = NonNullable<
  SocialCampaignReportResponse["ad_creative_breakdown"]
>[number];

type CampaignAdSetEntry = {
  adSet: SocialAdSet;
  isExcluded: boolean;
};

type VisibleSocialReportData = {
  filteredCampaigns: SocialCampaign[];
  filteredAdSets: SocialAdSet[];
  excludedInfluencerAdSets: SocialAdSet[];
  adSetsByCampaign: Map<string, CampaignAdSetEntry[]>;
  adCreativeRecords: SocialAdCreativeRecord[];
};

type AdSetTotals = {
  reach: number;
  impressions: number;
  spend: number;
  engagement: number;
  landing_page_views: number;
};

type CampaignInteractionAdjustment = {
  reactions: number;
  comments: number;
  shares: number;
  includedEngagement: number;
  includedCount: number;
};

type CampaignInteractionAdjustmentResult = {
  adjustments: Map<string, CampaignInteractionAdjustment>;
  totalReactions: number;
  totalComments: number;
  totalShares: number;
};

export const EXCLUDED_INFLUENCER_AD_NAMES = new Set<string>([
  "Allie.Eats",
  "itskimtish",
  "gwynethyuma",
  "chefshota",
]);

const normalizeAdCreativeName = (name: string): string => {
  let normalized = name.trim();
  const hyphenCopyPattern = /\s*(?:-|–|—)\s*copy(?:\s*\d+)?$/i;
  const parenCopyPattern = /\s*\(copy(?:\s*\d+)?\)$/i;

  const applyPatterns = () => {
    const updated = normalized
      .replace(hyphenCopyPattern, "")
      .replace(parenCopyPattern, "");
    if (updated !== normalized) {
      normalized = updated.trim();
      applyPatterns();
    }
  };

  applyPatterns();

  return normalized.replace(/\s{2,}/g, " ").trim();
};

const parseNumericValue = (
  value: string | number | null | undefined,
): number => {
  if (value === null || value === undefined || value === "") {
    return 0;
  }
  const numeric =
    typeof value === "number" ? value : Number.parseFloat(String(value));
  return Number.isFinite(numeric) ? numeric : 0;
};

const isArchiveCampaign = (campaignName: string) =>
  campaignName.toLowerCase().startsWith("archive");

const emptyAdSetTotals = (): AdSetTotals => ({
  reach: 0,
  impressions: 0,
  spend: 0,
  engagement: 0,
  landing_page_views: 0,
});

const deriveVisibleSocialReportData = (
  data: SocialCampaignReportResponse,
  options: {
    campaignView: SocialCampaignView;
    excludeInfluencerAds: boolean;
  },
): VisibleSocialReportData => {
  const { campaignView, excludeInfluencerAds } = options;
  const filteredCampaigns = data.campaigns.filter((campaign) => {
    if (campaignView === "archive") {
      return isArchiveCampaign(campaign.campaign_name);
    }
    if (campaignView === "active") {
      return !isArchiveCampaign(campaign.campaign_name);
    }
    return true;
  });

  const filteredCampaignNames = new Set<string>(
    filteredCampaigns.map((campaign) => campaign.campaign_name),
  );

  const filteredAdSets: SocialAdSet[] = [];
  const excludedInfluencerAdSets: SocialAdSet[] = [];
  const adSetsByCampaign = new Map<string, CampaignAdSetEntry[]>();

  data.ad_sets.forEach((adSet) => {
    if (!filteredCampaignNames.has(adSet.campaign_name)) {
      return;
    }

    const containsExcludedInfluencer =
      Array.isArray(adSet.ad_names) &&
      adSet.ad_names.some((name) => EXCLUDED_INFLUENCER_AD_NAMES.has(name));

    const isExcluded = Boolean(excludeInfluencerAds && containsExcludedInfluencer);
    if (isExcluded) {
      excludedInfluencerAdSets.push(adSet);
    } else {
      filteredAdSets.push(adSet);
    }

    const list = adSetsByCampaign.get(adSet.campaign_name) ?? [];
    list.push({ adSet, isExcluded });
    adSetsByCampaign.set(adSet.campaign_name, list);
  });

  const includedAdSetNames = new Set(
    filteredAdSets.map((adSet) => adSet.ad_set_name),
  );
  const adCreativeRecords = (data.ad_creative_breakdown ?? []).filter(
    (record) => {
      if (!filteredCampaignNames.has(record.campaign_name)) {
        return false;
      }
      if (record.ad_set_name && !includedAdSetNames.has(record.ad_set_name)) {
        return false;
      }
      if (excludeInfluencerAds && EXCLUDED_INFLUENCER_AD_NAMES.has(record.ad_name)) {
        return false;
      }
      return true;
    },
  );

  return {
    filteredCampaigns,
    filteredAdSets,
    excludedInfluencerAdSets,
    adSetsByCampaign,
    adCreativeRecords,
  };
};

const sumExcludedAdSets = (adSets: SocialAdSet[]): AdSetTotals =>
  adSets.reduce<AdSetTotals>((totals, adSet) => {
    totals.reach += adSet.total_reach;
    totals.impressions += adSet.total_impressions;
    totals.spend += parseNumericValue(adSet.total_spend);
    totals.engagement += adSet.total_engagement;
    totals.landing_page_views += adSet.total_landing_page_views;
    return totals;
  }, emptyAdSetTotals());

const buildCreativeViews = (
  adCreativeRecords: SocialAdCreativeRecord[],
): {
  aggregatedAdCreatives: AggregatedAdCreative[];
  audienceCreativePerformance: AudienceCreativePerformance[];
} => {
  const adCreativeAggregateMap = new Map<
    string,
    {
      ad_name: string;
      total_reach: number;
      total_impressions: number;
      total_spend: number;
      total_engagement: number;
      total_landing_page_views: number;
      total_reactions: number;
      total_comments: number;
      total_shares: number;
      campaign_names: Set<string>;
      ad_set_names: Set<string>;
      platforms: Set<string>;
      total_clicks: number;
      variant_names: Set<string>;
    }
  >();
  const audienceCreativeMap = new Map<
    string,
    Map<
      string,
      {
        ad_name: string;
        total_reach: number;
        total_impressions: number;
        total_engagement: number;
        total_spend: number;
        total_clicks: number;
      }
    >
  >();

  adCreativeRecords.forEach((record) => {
    const normalizedName = normalizeAdCreativeName(record.ad_name);
    const current = adCreativeAggregateMap.get(normalizedName) ?? {
      ad_name: normalizedName,
      total_reach: 0,
      total_impressions: 0,
      total_spend: 0,
      total_engagement: 0,
      total_landing_page_views: 0,
      total_reactions: 0,
      total_comments: 0,
      total_shares: 0,
      campaign_names: new Set<string>(),
      ad_set_names: new Set<string>(),
      platforms: new Set<string>(),
      total_clicks: 0,
      variant_names: new Set<string>(),
    };

    current.total_reach += record.total_reach;
    current.total_impressions += record.total_impressions;
    const spendValue = Number.parseFloat(record.total_spend ?? "0");
    current.total_spend += Number.isFinite(spendValue) ? spendValue : 0;
    current.total_engagement += record.total_engagement;
    current.total_landing_page_views += record.total_landing_page_views;
    current.total_reactions += record.total_reactions;
    current.total_comments += record.total_comments;
    current.total_shares += record.total_shares;
    current.campaign_names.add(record.campaign_name);
    if (record.ad_set_name) {
      current.ad_set_names.add(record.ad_set_name);
    }
    current.platforms.add(record.platform);
    current.total_clicks += parseNumericValue(record.total_clicks);
    current.variant_names.add(record.ad_name);

    adCreativeAggregateMap.set(normalizedName, current);

    if (!record.ad_set_name) {
      return;
    }

    const audienceName = record.ad_set_name;
    if (!audienceCreativeMap.has(audienceName)) {
      audienceCreativeMap.set(audienceName, new Map());
    }
    const creativeMap = audienceCreativeMap.get(audienceName)!;
    const audienceCurrent = creativeMap.get(normalizedName) ?? {
      ad_name: normalizedName,
      total_reach: 0,
      total_impressions: 0,
      total_engagement: 0,
      total_spend: 0,
      total_clicks: 0,
    };

    audienceCurrent.total_reach += record.total_reach;
    audienceCurrent.total_impressions += record.total_impressions;
    audienceCurrent.total_engagement += record.total_engagement;
    const spendValueAudience = Number.parseFloat(record.total_spend ?? "0");
    audienceCurrent.total_spend += Number.isFinite(spendValueAudience)
      ? spendValueAudience
      : 0;
    audienceCurrent.total_clicks += parseNumericValue(record.total_clicks);

    creativeMap.set(normalizedName, audienceCurrent);
  });

  return {
    aggregatedAdCreatives: Array.from(
    adCreativeAggregateMap.values(),
    ).map((entry) => ({
      ad_name: entry.ad_name,
      total_reach: entry.total_reach,
      total_impressions: entry.total_impressions,
      total_spend: entry.total_spend,
      total_clicks: entry.total_clicks,
      total_engagement: entry.total_engagement,
      total_landing_page_views: entry.total_landing_page_views,
      total_reactions: entry.total_reactions,
      total_comments: entry.total_comments,
      total_shares: entry.total_shares,
      campaign_names: Array.from(entry.campaign_names),
      ad_set_names: Array.from(entry.ad_set_names),
      platforms: Array.from(entry.platforms),
      avg_ctr:
        entry.total_impressions > 0
          ? entry.total_clicks / entry.total_impressions
          : null,
      variant_names: Array.from(entry.variant_names),
    })),
    audienceCreativePerformance: Array.from(audienceCreativeMap.entries()).map(
      ([audienceName, creativeMap]) => {
        const creatives = Array.from(creativeMap.values()).map((creative) => ({
          ad_name: creative.ad_name,
          total_reach: creative.total_reach,
          total_impressions: creative.total_impressions,
          total_engagement: creative.total_engagement,
          total_spend: creative.total_spend,
          total_clicks: creative.total_clicks,
          avg_ctr:
            creative.total_impressions > 0
              ? creative.total_clicks / creative.total_impressions
              : null,
        }));
        const totalReach = creatives.reduce(
          (sum, item) => sum + item.total_reach,
          0,
        );
        const totalImpressions = creatives.reduce(
          (sum, item) => sum + item.total_impressions,
          0,
        );
        const totalEngagement = creatives.reduce(
          (sum, item) => sum + item.total_engagement,
          0,
        );

        creatives.sort((a, b) => b.total_impressions - a.total_impressions);

        return {
          ad_set_name: audienceName,
          total_reach: totalReach,
          total_impressions: totalImpressions,
          total_engagement: totalEngagement,
          creatives,
        };
      },
    ),
  };
};

const calculateInteractionAdjustments = (
  campaigns: SocialCampaign[],
  adSetsByCampaign: Map<string, CampaignAdSetEntry[]>,
): CampaignInteractionAdjustmentResult => {
  const adjustments = new Map<string, CampaignInteractionAdjustment>();
  let totalReactions = 0;
  let totalComments = 0;
  let totalShares = 0;

  campaigns.forEach((campaign) => {
    const campaignAdSets = adSetsByCampaign.get(campaign.campaign_name) ?? [];
    const totalEngagementAll = campaignAdSets.reduce(
      (sum, { adSet }) => sum + adSet.total_engagement,
      0,
    );
    const includedEntries = campaignAdSets.filter((entry) => !entry.isExcluded);
    const includedEngagement = includedEntries.reduce(
      (sum, { adSet }) => sum + adSet.total_engagement,
      0,
    );
    const includedCount = includedEntries.length;
    const ratio =
      totalEngagementAll > 0 ? includedEngagement / totalEngagementAll : 1;

    const reactions = campaign.total_reactions * ratio;
    const comments = campaign.total_comments * ratio;
    const shares = campaign.total_shares * ratio;

    totalReactions += reactions;
    totalComments += comments;
    totalShares += shares;

    adjustments.set(campaign.campaign_name, {
      reactions,
      comments,
      shares,
      includedEngagement,
      includedCount,
    });
  });

  return {
    adjustments,
    totalReactions,
    totalComments,
    totalShares,
  };
};

const buildSummaryTotals = (
  data: SocialCampaignReportResponse,
  filteredCampaigns: SocialCampaign[],
  filteredAdSets: SocialAdSet[],
  excludedTotals: AdSetTotals,
  interactionAdjustments: CampaignInteractionAdjustmentResult,
  platformCount: number,
) => {
  const baseTotalReach = filteredCampaigns.reduce(
    (sum, campaign) => sum + campaign.total_reach,
    0,
  );
  const baseTotalImpressions = filteredCampaigns.reduce(
    (sum, campaign) => sum + campaign.total_impressions,
    0,
  );
  const baseTotalSpend = filteredCampaigns.reduce(
    (sum, campaign) => sum + parseFloat(campaign.total_spend),
    0,
  );
  const baseTotalEngagement = filteredCampaigns.reduce(
    (sum, campaign) => sum + campaign.total_engagement,
    0,
  );
  const baseTotalLandingPageViews = filteredCampaigns.reduce(
    (sum, campaign) => sum + campaign.total_landing_page_views,
    0,
  );

  const totalReach = Math.max(0, Math.round(baseTotalReach - excludedTotals.reach));
  const totalImpressions = Math.max(
    0,
    Math.round(baseTotalImpressions - excludedTotals.impressions),
  );
  const totalSpend = Math.max(0, baseTotalSpend - excludedTotals.spend);
  const totalEngagement = Math.max(
    0,
    Math.round(baseTotalEngagement - excludedTotals.engagement),
  );
  const totalReactions = Math.max(0, Math.round(interactionAdjustments.totalReactions));
  const totalComments = Math.max(0, Math.round(interactionAdjustments.totalComments));
  const totalShares = Math.max(0, Math.round(interactionAdjustments.totalShares));
  const totalLandingPageViews = Math.max(
    0,
    Math.round(baseTotalLandingPageViews - excludedTotals.landing_page_views),
  );

  const avgFrequency = totalReach > 0 ? totalImpressions / totalReach : 0;
  const engagementRate = totalReach > 0 ? totalEngagement / totalReach : 0;
  const avgCPM =
    totalImpressions > 0 ? (totalSpend / totalImpressions) * 1000 : 0;
  const ctrValues = filteredAdSets
    .filter((adSet) => adSet.avg_ctr)
    .map((adSet) => parseFloat(adSet.avg_ctr ?? "0"))
    .filter((value) => Number.isFinite(value));
  const avgCTR =
    ctrValues.length > 0
      ? ctrValues.reduce((sum, ctr) => sum + ctr, 0) / ctrValues.length
      : null;

  return {
    total_reach: totalReach,
    total_impressions: totalImpressions,
    avg_frequency: avgFrequency.toFixed(16),
    total_spend: totalSpend.toFixed(2),
    total_engagement: totalEngagement,
    total_reactions: totalReactions,
    total_comments: totalComments,
    total_shares: totalShares,
    engagement_rate: engagementRate.toFixed(20),
    total_landing_page_views: totalLandingPageViews,
    avg_ctr: avgCTR ? avgCTR.toFixed(20) : null,
    avg_cpm: avgCPM.toFixed(20),
    date_range_start: data.summary.date_range_start,
    date_range_end: data.summary.date_range_end,
    total_days: data.summary.total_days,
    campaign_count: new Set(filteredAdSets.map((adSet) => adSet.campaign_name))
      .size,
    ad_set_count: filteredAdSets.length,
    platform_count: platformCount,
    total_followers:
      data.summary.total_followers !== null &&
      data.summary.total_followers !== undefined
        ? Number(data.summary.total_followers)
        : null,
  };
};

const buildPlatformSummaries = (
  filteredCampaigns: SocialCampaign[],
  filteredAdSets: SocialAdSet[],
  adSetsByCampaign: Map<string, CampaignAdSetEntry[]>,
  interactionAdjustments: Map<string, CampaignInteractionAdjustment>,
) => {
  const platformMap = new Map<
    string,
    {
      platform: string;
      total_reach: number;
      total_impressions: number;
      total_spend: number;
      total_engagement: number;
      total_landing_page_views: number;
      ctr_values: number[];
      campaign_names: Set<string>;
      total_reactions: number;
      total_comments: number;
      total_shares: number;
      ad_set_count: number;
      followers_values: Set<number>;
    }
  >();

  filteredAdSets.forEach((adSet) => {
    const platform = adSet.platform;
    if (!platformMap.has(platform)) {
      platformMap.set(platform, {
        platform,
        total_reach: 0,
        total_impressions: 0,
        total_spend: 0,
        total_engagement: 0,
        total_landing_page_views: 0,
        ctr_values: [],
        campaign_names: new Set<string>(),
        total_reactions: 0,
        total_comments: 0,
        total_shares: 0,
        ad_set_count: 0,
        followers_values: new Set<number>(),
      });
    }
    const entry = platformMap.get(platform)!;
    entry.total_reach += adSet.total_reach;
    entry.total_impressions += adSet.total_impressions;
    entry.total_spend += parseFloat(adSet.total_spend);
    entry.total_engagement += adSet.total_engagement;
    entry.total_landing_page_views += adSet.total_landing_page_views;
    entry.campaign_names.add(adSet.campaign_name);
    entry.ad_set_count += 1;
    if (adSet.avg_ctr) {
      entry.ctr_values.push(parseFloat(adSet.avg_ctr));
    }
    if (adSet.total_followers !== undefined && adSet.total_followers !== null) {
      const followerNumber = Number(adSet.total_followers);
      if (Number.isFinite(followerNumber) && followerNumber > 0) {
        entry.followers_values.add(followerNumber);
      }
    }
  });

  filteredCampaigns.forEach((campaign) => {
    const campaignAdSets = adSetsByCampaign.get(campaign.campaign_name);
    if (!campaignAdSets || campaignAdSets.length === 0) {
      return;
    }

    const adjustment = interactionAdjustments.get(campaign.campaign_name);
    const includedEntries = campaignAdSets.filter((entry) => !entry.isExcluded);
    const includedEngagement =
      adjustment?.includedEngagement ??
      includedEntries.reduce((sum, { adSet }) => sum + adSet.total_engagement, 0);
    const includedCount = adjustment?.includedCount ?? includedEntries.length;
    const shareDenominator =
      includedEngagement > 0 ? includedEngagement : includedCount;

    campaignAdSets.forEach(({ adSet, isExcluded }) => {
      if (isExcluded) {
        return;
      }
      const platformEntry = platformMap.get(adSet.platform);
      if (!platformEntry) {
        return;
      }
      const share =
        shareDenominator > 0
          ? includedEngagement > 0
            ? adSet.total_engagement / shareDenominator
            : 1 / shareDenominator
          : 0;

      const reactionsSource = adjustment?.reactions ?? campaign.total_reactions;
      const commentsSource = adjustment?.comments ?? campaign.total_comments;
      const sharesSource = adjustment?.shares ?? campaign.total_shares;

      platformEntry.total_reactions += reactionsSource * share;
      platformEntry.total_comments += commentsSource * share;
      platformEntry.total_shares += sharesSource * share;
    });
  });

  return Array.from(platformMap.values()).map((entry) => ({
    platform: entry.platform,
    total_reach: entry.total_reach,
    total_impressions: entry.total_impressions,
    total_spend: entry.total_spend.toFixed(2),
    total_engagement: entry.total_engagement,
    total_reactions: Math.round(entry.total_reactions),
    total_comments: Math.round(entry.total_comments),
    total_shares: Math.round(entry.total_shares),
    total_landing_page_views: entry.total_landing_page_views,
    total_followers:
      entry.followers_values.size > 0
        ? Math.max(...Array.from(entry.followers_values))
        : null,
    engagement_rate: (entry.total_reach > 0
      ? entry.total_engagement / entry.total_reach
      : 0
    ).toFixed(10),
    avg_ctr:
      entry.ctr_values.length > 0
        ? (
            entry.ctr_values.reduce((sum, value) => sum + value, 0) /
            entry.ctr_values.length
          ).toFixed(20)
        : null,
    cpm: (entry.total_impressions > 0
      ? (entry.total_spend / entry.total_impressions) * 1000
      : 0
    ).toFixed(20),
    campaign_count: entry.campaign_names.size,
    ad_set_count: entry.ad_set_count,
  }));
};

export function buildSocialLayoutViewModel(
  data: SocialCampaignReportResponse,
  options: {
    campaignView: SocialCampaignView;
    excludeInfluencerAds: boolean;
  },
) {
  const {
    filteredCampaigns,
    filteredAdSets,
    excludedInfluencerAdSets,
    adSetsByCampaign,
    adCreativeRecords,
  } = deriveVisibleSocialReportData(data, options);

  const excludedTotals = sumExcludedAdSets(excludedInfluencerAdSets);
  const { aggregatedAdCreatives, audienceCreativePerformance } =
    buildCreativeViews(adCreativeRecords);
  const interactionAdjustments = calculateInteractionAdjustments(
    filteredCampaigns,
    adSetsByCampaign,
  );
  const filteredPlatforms = buildPlatformSummaries(
    filteredCampaigns,
    filteredAdSets,
    adSetsByCampaign,
    interactionAdjustments.adjustments,
  );
  const summary = buildSummaryTotals(
    data,
    filteredCampaigns,
    filteredAdSets,
    excludedTotals,
    interactionAdjustments,
    filteredPlatforms.length,
  );

  return {
    summary,
    platforms: filteredPlatforms,
    campaigns: filteredCampaigns,
    ad_sets: filteredAdSets,
    ad_creatives: aggregatedAdCreatives,
    audience_creatives: audienceCreativePerformance,
    daily_metrics: data.daily_metrics,
  };
}
